import { FC, useEffect, useState } from "react";

import { Box, Drawer, Stack, useTheme } from "@mui/material";
import NextLink from "next/link";

import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const GRANT_FINDER_HOME = "/grant-finder";
const ZEFFY_REGISTER = "https://www.zeffy.com/register";
const ZEFFY_LOGO_SRC =
  "https://cdn.prod.website-files.com/60af7f6d21134db12548f5b9/624c9c5e2bee8a35a28be92a_Zeffy-Logo-Color.svg";

const MENU_ITEMS = [
  {
    label: "What is Zeffy",
    description: "Learn more about the only zero-fee fundraising platform.",
    href: "https://www.zeffy.com/",
  },
  {
    label: "How is Zeffy free",
    description: "Every feature we offer is 100% free. We even cover transaction fees.",
    href: "https://www.zeffy.com/home/free-online-fundraising-platform",
  },
  {
    label: "See how Zeffy compares",
    description: "Compare Zeffy to popular nonprofit tools and software.",
    href: "https://www.zeffy.com/home/compare",
  },
];

const FOOTER_LINKS = [
  { label: "About", href: "https://www.zeffy.com/home/about-us" },
  { label: "Press", href: "https://www.zeffy.com/home/press" },
  { label: "Legal & Privacy", href: "https://support.zeffy.com/legal-data-privacy-security" },
];

export const Navbar: FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "";
    }
    return () => {
      document.body.style.overflowY = "";
    };
  }, [open]);

  return (
    <Box
      component="header"
      sx={{
        position: "static",
        backgroundColor: "#F8F8FF",
        borderBottom: "1px solid transparent",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          maxWidth: 1312,
          mx: "auto",
          px: { xs: 2.5, md: 5 },
          py: 2,
        }}
      >
        <Box
          component={NextLink}
          href={GRANT_FINDER_HOME}
          sx={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
        >
          <Box
            component="img"
            src={ZEFFY_LOGO_SRC}
            alt="Zeffy"
            sx={{ width: { xs: 80, sm: 120 }, height: "auto", display: "block" }}
          />
        </Box>

        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            sx={{
              width: 42,
              height: 42,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(85,85,231,0.35)",
              borderRadius: 1,
              backgroundColor: "transparent",
              cursor: "pointer",
              transition: "background-color 200ms ease",
              "&:hover": { backgroundColor: "rgba(85,85,231,0.08)" },
              flexShrink: 0,
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              sx={{ width: 20, height: 20 }}
              fill="none"
              stroke="#5555E7"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </Box>
          </Box>
        </Stack>
      </Stack>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480 },
            backgroundColor: "#F8F8FF",
            p: { xs: 3, sm: 5 },
          },
        }}
      >
        <Stack spacing={5} sx={{ height: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box
              component={NextLink}
              href={GRANT_FINDER_HOME}
              onClick={() => setOpen(false)}
              sx={{ display: "inline-flex", textDecoration: "none" }}
            >
              <Box component="img" src={ZEFFY_LOGO_SRC} alt="Zeffy" sx={{ width: 95, height: "auto" }} />
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              sx={{
                width: 40,
                height: 40,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(85,85,231,0.35)",
                borderRadius: 1,
                backgroundColor: "transparent",
                cursor: "pointer",
                transition: "background-color 200ms ease",
                "&:hover": { backgroundColor: "rgba(85,85,231,0.08)" },
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{ width: 18, height: 18 }}
                fill="none"
                stroke="#5555E7"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="6" y1="18" x2="18" y2="6" />
              </Box>
            </Box>
          </Stack>

          <Stack spacing={2}>
            {MENU_ITEMS.map((item) => (
              <Box
                key={item.label}
                component="a"
                href={item.href}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                sx={{
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  px: 2,
                  py: 2,
                  borderRadius: 1.5,
                  transition: "background-color 160ms ease",
                  "&:hover": { backgroundColor: "rgba(85,85,231,0.06)" },
                }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <DashboardTypography variant="subtitle1" vibe="text-neutral-intense">
                      {item.label}
                    </DashboardTypography>
                    <Box component="span" sx={{ color: "#5555E7" }}>
                      →
                    </Box>
                  </Stack>
                  <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                    {item.description}
                  </DashboardTypography>
                </Stack>
              </Box>
            ))}
          </Stack>

          <Box
            component="a"
            href={ZEFFY_REGISTER}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              alignSelf: "stretch",
              backgroundColor: "#5555E7",
              color: theme.palette.common.white,
              px: 4,
              py: 2.25,
              borderRadius: 1,
              textDecoration: "none",
              transition: "background-color 200ms ease",
              "&:hover": { backgroundColor: "#4848C4" },
            }}
          >
            <DashboardTypography variant="body1" sx={{ color: "inherit", fontWeight: 500 }}>
              Start fundraising for free
            </DashboardTypography>
          </Box>

          <Box sx={{ mt: "auto", display: { xs: "none", sm: "block" } }}>
            <Stack direction="row" spacing={4} sx={{ pt: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Stack spacing={1}>
                <DashboardTypography variant="subtitle2" vibe="text-neutral-intense">
                  About Zeffy
                </DashboardTypography>
                {FOOTER_LINKS.map((link) => (
                  <Box
                    key={link.label}
                    component="a"
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                      {link.label}
                    </DashboardTypography>
                  </Box>
                ))}
              </Stack>
            </Stack>
            <DashboardTypography variant="caption" vibe="text-neutral-quiet" sx={{ pt: 2, display: "block" }}>
              © {new Date().getFullYear()} Zeffy, inc. All rights reserved.
            </DashboardTypography>
          </Box>
        </Stack>
      </Drawer>
    </Box>
  );
};
